import React, { createContext, useContext, useState, useRef, useMemo } from 'react';
import { Toast, createToast, ToastContainer } from '../components/common/Toast';

interface ToastContextType {
  showToast: (message: string, type?: 'success' | 'error' | 'info', duration?: number) => void;
  showSuccess: (message: string, duration?: number) => void;
  showError: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const handlersRef = useRef<ToastContextType | null>(null);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info', duration?: number) => {
    const toast = createToast(message, type, duration);
    setToasts((prev) => [...prev, toast]);
  };

  const showSuccess = (message: string, duration?: number) => {
    showToast(message, 'success', duration);
  };

  const showError = (message: string, duration?: number) => {
    showToast(message, 'error', duration);
  };

  handlersRef.current = { showToast, showSuccess, showError };

  const value = useMemo<ToastContextType>(
    () => ({
      showToast: (m, t, d) => handlersRef.current?.showToast(m, t, d),
      showSuccess: (m, d) => handlersRef.current?.showSuccess(m, d),
      showError: (m, d) => handlersRef.current?.showError(m, d),
    }),
    []
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </ToastContext.Provider>
  );
};








